import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlainDate } from 'temporal-luxon';
import { Button, DatePicker, Input, Select, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { AuthContext } from 'libs.react_internal';
import { useServerSideCubeQuery, } from '../../../data_reports/hooks/use_server_side_cube_query';
import { getCubeApi } from '../../../transfer_templates/hooks/use_cube_api/use_cube_api.hook';
import { getOperatorLabel, isFreeTextOperator, isNumericFieldType, isRangeOperator, normalizeCubeFilterParam, } from '../../utils';
import { NumericFilterControl } from '../numeric_filter_control';
const PAGE_SIZE = 20;
export const DashboardFilters = ({ filters, studyId, environmentId, runCount, cubeConfigIds = [], onApply, }) => {
    const translate = useLocalizeMessage();
    const [selectedFilters, setSelectedFilters] = useState({});
    const [selectedOperators, setSelectedOperators] = useState({});
    const prevDefaultsKeyRef = useRef('');
    const sortedFilters = useMemo(() => [...filters].sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity)), [filters]);
    const defaultsKey = useMemo(() => JSON.stringify(filters.map((f) => ({
        k: f.key,
        dv: f.defaultValues,
        op: f.defaultOperator || f.operator,
    }))), [filters]);
    const operatorByKey = useMemo(() => {
        const map = {};
        for (const f of filters) {
            map[f.key] = f.defaultOperator || f.operator || 'equals';
        }
        return map;
    }, [filters]);
    useEffect(() => {
        if (defaultsKey === prevDefaultsKeyRef.current) {
            return;
        }
        prevDefaultsKeyRef.current = defaultsKey;
        const defaults = {};
        const operators = { ...operatorByKey };
        for (const f of filters) {
            if (f.defaultValues?.length) {
                defaults[f.key] = f.defaultValues.map((v) => ({ label: v, value: v }));
            }
        }
        setSelectedFilters(defaults);
        setSelectedOperators(operators);
    }, [defaultsKey, filters, operatorByKey]);
    const handleApply = useCallback(() => {
        const filterParams = Object.entries(selectedFilters)
            // Range operators keep two slots (from / to); drop a filter when the user
            // has only supplied one of them so we never send `[value, '']` to Cube.js.
            .filter(([, value]) => Array.isArray(value) && value.length > 0 && value.every((v) => v.value))
            .map(([key, value]) => normalizeCubeFilterParam({
            member: key,
            operator: selectedOperators[key] || operatorByKey[key] || 'equals',
            values: value.map((v) => v.value),
        }));
        onApply(filterParams);
    }, [selectedFilters, selectedOperators, onApply, operatorByKey]);
    const handleClear = useCallback(() => {
        setSelectedFilters({});
        setSelectedOperators({ ...operatorByKey });
        onApply([]);
    }, [onApply, operatorByKey]);
    return (_jsxs("div", { className: 'flex md:items-end justify-between mb-4 gap-4 px-4 flex-col md:flex-row flex-wrap', children: [_jsx("div", { className: 'flex-1 flex gap-4 flex-col md:flex-row flex-wrap', children: sortedFilters.map((filter) => (_jsx(FilterSelect, { filter: filter, studyId: studyId, environmentId: environmentId, runCount: runCount, cubeConfigIds: cubeConfigIds, selectedOptions: selectedFilters[filter.key] || [], onChange: (options) => setSelectedFilters((prev) => ({ ...prev, [filter.key]: options })), selectedOperator: selectedOperators[filter.key] || operatorByKey[filter.key] || 'equals', onOperatorChange: (operator) => setSelectedOperators((prev) => ({ ...prev, [filter.key]: operator })) }, filter.key))) }), _jsxs("div", { className: 'flex items-center gap-2', children: [_jsx(Button, { label: translate('Clear filters'), variant: 'transparent', onClick: handleClear }), _jsx(Button, { label: translate('Apply filters'), variant: 'secondary', onClick: handleApply })] })] }));
};
const FilterSelect = ({ filter, studyId, environmentId, runCount, cubeConfigIds, selectedOptions, onChange, selectedOperator, onOperatorChange, }) => {
    const translate = useLocalizeMessage();
    const { entityId } = useContext(AuthContext);
    const isTimeField = filter.fieldType === 'time';
    const isNumericField = isNumericFieldType(filter.fieldType);
    const effectiveOperator = filter.defaultOperator || filter.operator;
    // `contains`-style operators take a user-typed substring rather than a known
    // dataset value, so we render a free-text input and skip loading options.
    const isFreeText = isFreeTextOperator(effectiveOperator);
    const displayLabel = useMemo(() => {
        if (isNumericField) {
            return filter.label;
        }
        const opLabel = getOperatorLabel(filter.fieldType, effectiveOperator);
        return opLabel ? `${filter.label} (${translate(opLabel)})` : filter.label;
    }, [filter.label, filter.fieldType, effectiveOperator, isNumericField, translate]);
    const cubeConfigIdsKey = cubeConfigIds.join(',');
    const filterQueryKey = JSON.stringify(filter.query);
    const mountKey = `${filter.key}|${filterQueryKey}|${studyId}|${environmentId}|${entityId ?? ''}|${cubeConfigIdsKey}|${runCount}`;
    const cubeApi = useMemo(() => {
        if (runCount < 1 || !studyId || !environmentId || !entityId) {
            return null;
        }
        return getCubeApi({
            entityId: entityId.toString(),
            studyId,
            activeReportsTab: 0,
            selectedTestEnv: { label: '', value: environmentId },
            prodOrgs: [],
            env: 'test',
            cubeConfigIds,
        });
    }, [runCount, studyId, environmentId, entityId, cubeConfigIdsKey, cubeConfigIds]);
    const isDateRange = isTimeField && isRangeOperator(selectedOperator);
    const parseDateValue = (raw) => {
        if (!raw) {
            return undefined;
        }
        try {
            return PlainDate.from(raw.split('T')[0]);
        }
        catch {
            return undefined;
        }
    };
    const singleDateValue = useMemo(() => {
        if (!isTimeField || isDateRange) {
            return undefined;
        }
        return parseDateValue(selectedOptions[0]?.value);
    }, [isTimeField, isDateRange, selectedOptions]);
    const fromDateValue = useMemo(() => (isDateRange ? parseDateValue(selectedOptions[0]?.value) : undefined), [isDateRange, selectedOptions]);
    const toDateValue = useMemo(() => (isDateRange ? parseDateValue(selectedOptions[1]?.value) : undefined), [isDateRange, selectedOptions]);
    const handleDateChange = useCallback((date) => {
        if (date) {
            const isoValue = date.toString();
            onChange([{ label: isoValue, value: isoValue }]);
        }
        else {
            onChange([]);
        }
    }, [onChange]);
    const handleRangeDateChange = useCallback((which, date) => {
        const iso = date?.toString() ?? '';
        const currentFrom = selectedOptions[0]?.value ?? '';
        const currentTo = selectedOptions[1]?.value ?? '';
        const nextFrom = which === 'from' ? iso : currentFrom;
        const nextTo = which === 'to' ? iso : currentTo;
        if (!nextFrom && !nextTo) {
            onChange([]);
            return;
        }
        onChange([
            { label: nextFrom, value: nextFrom },
            { label: nextTo, value: nextTo },
        ]);
    }, [onChange, selectedOptions]);
    const selectionLabel = useMemo(() => {
        const labels = selectedOptions.map(({ label }) => label);
        if (labels.length > 1) {
            return `${labels.slice(0, 1).join(', ')} (+${labels.length - 1} more)`;
        }
        return labels.join(', ');
    }, [selectedOptions]);
    if (isTimeField) {
        return (_jsxs("div", { children: [displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-mb-2', children: displayLabel })), isDateRange ? (_jsxs("div", { className: 'grid grid-cols-2 gap-2', children: [_jsx(DatePicker, { id: `date-filter-from-${filter.key}`, value: fromDateValue, onChange: (date) => handleRangeDateChange('from', date), width: 'full' }), _jsx(DatePicker, { id: `date-filter-to-${filter.key}`, value: toDateValue, onChange: (date) => handleRangeDateChange('to', date), width: 'full' })] })) : (_jsx(DatePicker, { id: `date-filter-${filter.key}`, value: singleDateValue, onChange: handleDateChange, width: 'sm' }))] }));
    }
    // Free-text operators (e.g. `contains`) accept user-typed substrings and don't
    // require a Cube fetch, so we render the Input even before the user clicks Run.
    if (isFreeText) {
        const freeTextValue = selectedOptions[0]?.value ?? '';
        return (_jsxs("div", { children: [displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-mb-2', children: displayLabel })), _jsx(Input, { id: `text-filter-${filter.key}`, value: freeTextValue, onChange: (e) => {
                        const next = e.target.value;
                        onChange(next ? [{ label: next, value: next }] : []);
                    }, width: 'sm', placeholder: translate('Enter text to match') })] }));
    }
    if (isNumericField) {
        const numericValue = selectedOptions[0]?.value ?? '';
        return (_jsx(NumericFilterControl, { filterKey: filter.key, label: displayLabel, operator: selectedOperator, onOperatorChange: onOperatorChange, value: numericValue, onValueChange: (next) => onChange(next ? [{ label: next, value: next }] : []), inputWidth: 'xs' }));
    }
    if (!cubeApi) {
        // No Cube calls until study/env are set and the user has run preview at least once (matches chart cards).
        return (_jsxs("div", { className: 'relative', children: [displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block nucleus-mb-2', children: displayLabel })), _jsx(Select, { options: [], value: selectedOptions, onChange: onChange, selectionLabel: selectionLabel, width: 'sm', multiple: true, searchableOptions: true, searchPlaceholder: translate('Search and press Enter...') }, filter.key)] }));
    }
    return (_jsx(FilterSelectPaginated, { filter: filter, displayLabel: displayLabel, cubeApi: cubeApi, selectedOptions: selectedOptions, onChange: onChange, translate: translate }, mountKey));
};
const FilterSelectPaginated = ({ filter, displayLabel, cubeApi, selectedOptions, onChange, translate, }) => {
    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: PAGE_SIZE,
    });
    const [accumulatedOptions, setAccumulatedOptions] = useState([]);
    const [exhausted, setExhausted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const activeSearchRef = useRef('');
    const prevResultRef = useRef(null);
    const waitingForLoadMoreRef = useRef(false);
    const loadMoreResolveRef = useRef(null);
    const query = useMemo(() => {
        const baseQuery = { ...filter.query, renewQuery: true };
        const filters = [...(baseQuery.filters || [])];
        if (searchTerm.trim()) {
            filters.push({
                member: filter.key,
                operator: 'contains',
                values: [searchTerm.trim()],
            });
        }
        return { ...baseQuery, filters };
    }, [filter.query, filter.key, searchTerm]);
    const { resultSet, isLoading, error, total } = useServerSideCubeQuery(query, {
        pagination: paginationState,
        cubeApi,
    });
    const hasMore = !exhausted && total > 0 && accumulatedOptions.length < total;
    useEffect(() => {
        if (!isLoading && waitingForLoadMoreRef.current && loadMoreResolveRef.current) {
            waitingForLoadMoreRef.current = false;
            const resolve = loadMoreResolveRef.current;
            loadMoreResolveRef.current = null;
            resolve();
        }
    }, [isLoading]);
    useEffect(() => {
        if (!resultSet || resultSet === prevResultRef.current) {
            return;
        }
        prevResultRef.current = resultSet;
        const rawRows = resultSet.tablePivot();
        const newOptions = rawRows
            .map((row) => {
            const value = row[filter.key];
            if (value === null || value === undefined) {
                return null;
            }
            return { label: String(value), value: String(value) };
        })
            .filter(Boolean);
        const isLastPage = rawRows.length < PAGE_SIZE;
        if (paginationState.pageIndex === 0) {
            setExhausted(isLastPage);
            if (filter.defaultValues?.length) {
                const loadedValueSet = new Set(newOptions.map((o) => o.value));
                const missingDefaults = filter.defaultValues
                    .filter((v) => !loadedValueSet.has(v))
                    .map((v) => ({ label: v, value: v }));
                setAccumulatedOptions([...missingDefaults, ...newOptions]);
            }
            else {
                setAccumulatedOptions(newOptions);
            }
        }
        else {
            if (newOptions.length > 0) {
                setAccumulatedOptions((prev) => [...prev, ...newOptions]);
            }
            if (isLastPage || newOptions.length === 0) {
                setExhausted(true);
            }
        }
    }, [resultSet, filter.key, filter.defaultValues, paginationState.pageIndex]);
    const handleLoadMore = useCallback(async () => {
        if (!hasMore || isLoading) {
            return;
        }
        waitingForLoadMoreRef.current = true;
        return new Promise((resolve) => {
            loadMoreResolveRef.current = resolve;
            setPaginationState((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
        });
    }, [hasMore, isLoading]);
    const handleLoadMoreRef = useRef(handleLoadMore);
    handleLoadMoreRef.current = handleLoadMore;
    const stableLoadMore = useCallback(() => handleLoadMoreRef.current(), []);
    const handleSearchOptions = useCallback((term) => {
        if (term === activeSearchRef.current) {
            return;
        }
        activeSearchRef.current = term;
        if (waitingForLoadMoreRef.current && loadMoreResolveRef.current) {
            waitingForLoadMoreRef.current = false;
            const resolve = loadMoreResolveRef.current;
            loadMoreResolveRef.current = null;
            resolve();
        }
        setExhausted(false);
        setPaginationState({ pageIndex: 0, pageSize: PAGE_SIZE });
        setAccumulatedOptions([]);
        setSearchTerm(term);
    }, []);
    const selectionLabel = useMemo(() => {
        const labels = selectedOptions.map(({ label }) => label);
        if (labels.length > 1) {
            return `${labels.slice(0, 1).join(', ')} (+${labels.length - 1} more)`;
        }
        return labels.join(', ');
    }, [selectedOptions]);
    const isInitialLoad = isLoading && accumulatedOptions.length === 0;
    return (_jsxs("div", { className: 'relative', children: [isInitialLoad && (_jsx("div", { className: 'absolute z-[5] inset-0 flex items-end justify-center size-full pb-1', children: _jsx(Spinner, {}) })), displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block nucleus-mb-2', children: displayLabel })), !isLoading && error && (_jsxs("div", { className: 'max-w-[250px] nucleus-font-sans nucleus-text-sm nucleus-text-red-500', children: [translate('No data'), " -- ", error?.message] })), !error && (_jsx(Select, { disabled: isInitialLoad, options: accumulatedOptions, value: selectedOptions, onChange: onChange, selectionLabel: selectionLabel, width: 'sm', multiple: true, searchableOptions: true, searchPlaceholder: translate('Search and press Enter...'), onSearchOptions: handleSearchOptions, onLoadMore: hasMore ? stableLoadMore : undefined }, filter.key))] }));
};
